<script lang="ts">
  import Button from '../ui/Button.svelte';
  import type { Integration, ConnectedIntegration } from '../../lib/types';

  type Props = {
    integration: Integration;
    connectionData: ConnectedIntegration | null;
    isConnecting: boolean;
    onConnect: (id: string) => void;
    onDisconnect: (id: string) => void;
    onConfigure: (id: string) => void;
    onViewHistory: (id: string) => void;
  };

  let { integration, connectionData, isConnecting, onConnect, onDisconnect, onConfigure, onViewHistory }: Props = $props();

  let isConnected = $derived(connectionData !== null);

  function formatLastSync(lastSyncAt: string | null): string {
    if (!lastSyncAt) return 'Never';
    const date = new Date(lastSyncAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  }

  function getConfigSummary(config: ConnectedIntegration['config']): string {
    const parts: string[] = [];
    if (config.syncOrders) {
      parts.push(`Orders: ${config.orderSyncDirection}`);
    }
    if (config.syncInventory) {
      parts.push(`Inventory: ${config.inventorySyncDirection}`);
    }
    return parts.length > 0 ? parts.join(' | ') : 'No sync configured';
  }
</script>

<div class="integration-card" class:coming-soon={!integration.enabled}>
  <div class="integration-icon">
    {#if integration.id === 'shopify'}
      <div class="icon-bg icon-shopify">
        <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
          <path d="M17.54 6.6c-.02-.13-.12-.2-.22-.21-.1-.01-2.14-.04-2.14-.04s-1.74-1.7-1.94-1.88c-.19-.19-.57-.13-.72-.09-.02.01-.37.12-.95.3-.56-1.62-1.55-3.11-3.3-3.11h-.15C7.68 1.05 7.13.93 6.66.93c-2.72 0-4.03 3.4-4.44 5.13-.71.22-1.22.38-1.28.4-.4.12-.41.13-.46.51C.44 7.24 0 10.86 0 10.86l9.72 1.83L17.54 6.6zM11.96 5.17c-.46.14-.97.3-1.5.47v-.34c0-1-.14-1.81-.37-2.42.92.12 1.53 1.16 1.87 2.29zM9.23 3.08c.25.57.41 1.38.41 2.49 0 .08 0 .15-.01.22-.73.23-1.52.47-2.33.72.45-1.73 1.29-2.57 1.93-3.43zM6.6 1.78c.13 0 .25.04.37.13C5.98 3.1 5.06 4.81 4.69 7.01L2.75 7.6C3.27 5.42 4.43 1.78 6.6 1.78z"/>
          <path d="M17.32 6.39s-.57-.12-.57-.12l-.39-.08c0-.01 0-.02-.01-.03-.28-1.43-.78-2.44-1.38-3.07.54.07.92.74 1.21 1.55.1.26.18.55.26.84.3.09.57.18.57.18.22.07.31.22.31.73z"/>
          <path d="M12.72 10.86l-1.78 5.44s-.66-.35-1.46-.35c-1.18 0-1.24.74-1.24.93 0 1.02 2.66 1.41 2.66 3.84 0 1.9-1.2 3.12-2.83 3.12-1.95 0-2.94-1.21-2.94-1.21l.52-1.72s1.02.88 1.89.88c.56 0 .79-.44.79-.77 0-1.34-2.18-1.4-2.18-3.61 0-1.86 1.33-3.65 4.03-3.65.72 0 1.08.14 1.54.1z"/>
        </svg>
      </div>
    {:else if integration.id === 'xero'}
      <div class="icon-bg icon-xero">
        <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
          <path d="M4.14 12l3.56-3.56c.2-.2.2-.52 0-.72s-.52-.2-.72 0L3.42 11.28.86 7.72c-.2-.2-.52-.2-.72 0s-.2.52 0 .72L3.7 12 .14 15.56c-.2.2-.2.52 0 .72.1.1.23.15.36.15s.26-.05.36-.15l3.28-3.28z"/>
          <path d="M10.14 8.44c-.28 0-.51.23-.51.51v6.1c0 .28.23.51.51.51s.51-.23.51-.51v-6.1c0-.28-.23-.51-.51-.51z" transform="translate(4 0)"/>
          <path d="M20.14 7.72c-.2-.2-.52-.2-.72 0L16.14 11l-3.28-3.28c-.2-.2-.52-.2-.72 0s-.2.52 0 .72L15.7 12l-3.56 3.56c-.2.2-.2.52 0 .72.1.1.23.15.36.15s.26-.05.36-.15l3.28-3.28 3.28 3.28c.1.1.23.15.36.15s.26-.05.36-.15c.2-.2.2-.52 0-.72L16.58 12l3.56-3.56c.2-.2.2-.52 0-.72z"/>
        </svg>
      </div>
    {:else if integration.id === 'woocommerce'}
      <div class="icon-bg icon-woocommerce">
        <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
          <path d="M2.4 1.2C1.08 1.2 0 2.28 0 3.6v12c0 1.32 1.08 2.4 2.4 2.4h6l3.6 3.6 3.6-3.6h6c1.32 0 2.4-1.08 2.4-2.4v-12C24 2.28 22.92 1.2 21.6 1.2H2.4zm3.12 3.6c.65 0 1.13.28 1.42.84.3.56.44 1.38.44 2.46 0 1.1-.16 1.96-.47 2.56-.31.6-.76.9-1.35.9-.64 0-1.11-.29-1.42-.87-.3-.58-.46-1.4-.46-2.47 0-1.1.15-1.94.46-2.54.31-.59.75-.88 1.38-.88zm5.76 0c.65 0 1.13.28 1.42.84.3.56.44 1.38.44 2.46 0 1.1-.16 1.96-.47 2.56-.31.6-.76.9-1.35.9-.64 0-1.11-.29-1.42-.87-.3-.58-.46-1.4-.46-2.47 0-1.1.15-1.94.46-2.54.31-.59.75-.88 1.38-.88zm5.94.12c.07 0 .14.02.2.06.1.06.16.18.16.3v.06l-1.14 5.16c-.04.18-.2.32-.38.32h-.06c-.15-.03-.27-.14-.33-.3L14.7 7.38l-.98 3.18c-.05.16-.18.27-.34.3h-.06c-.18 0-.34-.14-.38-.32L11.8 5.4c-.01-.05-.01-.08 0-.12.02-.16.14-.28.3-.3h.06c.18 0 .33.12.38.3l.84 3.9 1.02-3.36c.04-.16.18-.28.34-.3h.1c.16.02.29.14.34.3l1.02 3.36.84-3.9c.04-.16.17-.28.32-.3h.06z"/>
        </svg>
      </div>
    {:else if integration.id === 'zapier'}
      <div class="icon-bg icon-zapier">
        <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
          <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm5.2 13.2h-3.4l2.4 2.4c-.2.4-.5.7-.8 1l-2.4-2.4v3.4h-1.4c-.2 0-.4 0-.6-.1v-3.3l-2.4 2.4c-.3-.3-.6-.6-.8-1l2.4-2.4H6.8c0-.2-.1-.4-.1-.6v-.8c0-.2 0-.4.1-.6h3.4L7.8 8.7c.2-.4.5-.7.8-1l2.4 2.4V6.7c.2 0 .4-.1.6-.1h.8c.2 0 .4 0 .6.1v3.4l2.4-2.4c.3.3.6.6.8 1l-2.4 2.4h3.4c0 .2.1.4.1.6v.8c0 .1 0 .3-.1.5z"/>
        </svg>
      </div>
    {:else}
      <div class="icon-bg icon-default">
        <span class="icon-letter">{integration.icon}</span>
      </div>
    {/if}
  </div>

  <div class="integration-info">
    <div class="integration-header">
      <h3>{integration.name}</h3>
      {#if !integration.enabled}
        <span class="coming-soon-badge">Coming Soon</span>
      {/if}
    </div>
    <p class="integration-description">{integration.description}</p>

    {#if integration.enabled && isConnected && connectionData}
      <div class="connection-status">
        <span class="status-connected">
          <span class="status-dot"></span>
          Connected
        </span>
        {#if connectionData.lastSyncAt}
          <span class="last-sync">Last sync: {formatLastSync(connectionData.lastSyncAt)}</span>
        {/if}
        {#if connectionData.lastSyncStatus}
          <span class="sync-status" class:sync-error={connectionData.lastSyncStatus === 'error'}>
            {connectionData.lastSyncStatus}
          </span>
        {/if}
      </div>
      <div class="config-summary">
        {getConfigSummary(connectionData.config)}
      </div>
    {/if}
  </div>

  <div class="integration-actions">
    {#if !integration.enabled}
      <!-- No actions for disabled integrations -->
    {:else if isConnected}
      <Button variant="secondary" size="small" onclick={() => onViewHistory(integration.id)}>
        History
      </Button>
      <Button variant="secondary" size="small" onclick={() => onConfigure(integration.id)}>
        Settings
      </Button>
      <Button variant="danger" size="small" onclick={() => onDisconnect(integration.id)}>
        Disconnect
      </Button>
    {:else}
      <Button
        variant="primary"
        size="small"
        disabled={isConnecting}
        loading={isConnecting}
        onclick={() => onConnect(integration.id)}
      >
        {isConnecting ? 'Connecting...' : 'Connect'}
      </Button>
    {/if}
  </div>
</div>

<style>
  .integration-card {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem;
    background: var(--color-surface, #f9fafb);
    border-radius: var(--radius-md, 8px);
    border: 1px solid var(--color-border, #e5e7eb);
    transition: border-color 0.2s;
  }

  .integration-card:hover:not(.coming-soon) {
    border-color: var(--color-border-hover, #d1d5db);
  }

  .integration-card.coming-soon {
    opacity: 0.6;
  }

  /* Icon backgrounds */
  .icon-bg {
    width: 48px;
    height: 48px;
    border-radius: var(--radius-md, 8px);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .icon-bg svg {
    width: 24px;
    height: 24px;
  }

  .icon-shopify {
    background: #95BF47;
  }

  .icon-xero {
    background: #13B5EA;
  }

  .icon-woocommerce {
    background: #96588A;
  }

  .icon-zapier {
    background: #FF4A00;
  }

  .icon-default {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }

  .icon-letter {
    color: white;
    font-weight: 700;
    font-size: 1.25rem;
  }

  /* Info section */
  .integration-info {
    flex: 1;
    min-width: 0;
  }

  .integration-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
  }

  .integration-header h3 {
    margin: 0;
    font-size: 1rem;
    color: var(--color-text, #333);
  }

  .integration-description {
    margin: 0 0 0.5rem;
    font-size: 0.85rem;
    color: var(--color-text-secondary, #666);
    line-height: 1.4;
  }

  .coming-soon-badge {
    font-size: 0.7rem;
    background: var(--color-surface-alt, #f3f4f6);
    color: var(--color-text-tertiary, #6b7280);
    padding: 0.15rem 0.5rem;
    border-radius: 12px;
    white-space: nowrap;
    font-weight: 500;
  }

  /* Connection status */
  .connection-status {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    font-size: 0.8rem;
    margin-bottom: 0.25rem;
  }

  .status-connected {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    color: #059669;
    font-weight: 500;
  }

  .status-dot {
    width: 6px;
    height: 6px;
    background: #10b981;
    border-radius: 50%;
  }

  .last-sync {
    color: var(--color-text-tertiary, #6b7280);
  }

  .sync-status {
    padding: 0.125rem 0.5rem;
    border-radius: 10px;
    background: #dbeafe;
    color: #1e40af;
    text-transform: capitalize;
    font-size: 0.75rem;
  }

  .sync-status.sync-error {
    background: #fee2e2;
    color: #991b1b;
  }

  .config-summary {
    font-size: 0.78rem;
    color: var(--color-text-tertiary, #6b7280);
    font-style: italic;
  }

  /* Actions */
  .integration-actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex-shrink: 0;
  }
</style>
